// 微信 WebView 深度集成（FR-3.2）：JS-SDK 服务端签名 → 原生分享 + 录音转写。
// 默认关闭（后端 ZEN_WECHAT_JSSDK_ENABLED=false）；非微信环境全部降级为 no-op。
import type { ClientConfig } from "./types";

interface WxShareData {
  title: string;
  desc: string;
  link: string;
  imgUrl: string;
}

// 最小化的 wx JS-SDK 类型
interface WxSDK {
  config(c: Record<string, unknown>): void;
  ready(cb: () => void): void;
  error(cb: (e: unknown) => void): void;
  updateAppMessageShareData?(o: Record<string, unknown>): void;
  updateTimelineShareData?(o: Record<string, unknown>): void;
  startRecord?(o?: Record<string, unknown>): void;
  stopRecord?(o: { success: (r: { localId: string }) => void; fail?: (e: unknown) => void }): void;
  translateVoice?(o: {
    localId: string;
    isShowProgressTips?: number;
    success: (r: { translateResult: string }) => void;
    fail?: (e: unknown) => void;
  }): void;
}

declare global {
  interface Window {
    wx?: WxSDK;
  }
}

const JSSDK_URL = "https://res.wx.qq.com/open/js/jweixin-1.6.0.js";

let wx: WxSDK | null = null;
let ready = false;
let readyResolvers: Array<() => void> = [];

export function inWeChat(): boolean {
  return /micromessenger/i.test(navigator.userAgent);
}

export function wxReady(): Promise<boolean> {
  if (ready) return Promise.resolve(true);
  if (!inWeChat()) return Promise.resolve(false);
  return new Promise((resolve) => {
    readyResolvers.push(() => resolve(true));
  });
}

export function getWx(): WxSDK | null {
  return ready ? wx : null;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.wx) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("jssdk load failed"));
    document.head.appendChild(s);
  });
}

const JS_APIS = [
  "updateAppMessageShareData",
  "updateTimelineShareData",
  "startRecord",
  "stopRecord",
  "translateVoice",
  "onVoiceRecordEnd",
];

// 启动微信集成；失败时静默降级（不影响主流程）
export async function setupWeChat(cfg: ClientConfig, share: WxShareData): Promise<void> {
  if (!inWeChat() || !cfg.wechat_jssdk_enabled) return;
  try {
    const url = location.href.split("#")[0];
    const r = await fetch(`/api/wechat/jssdk-config?url=${encodeURIComponent(url)}`);
    if (!r.ok) return; // 未配置公众号 → 放弃
    const conf = (await r.json()) as Record<string, unknown>;
    await loadScript(JSSDK_URL);
    wx = window.wx ?? null;
    if (!wx) return;

    wx.config({ debug: false, jsApiList: JS_APIS, ...conf });
    wx.error(() => {
      /* 签名失败：保持降级 */
    });
    wx.ready(() => {
      ready = true;
      updateShare(share);
      readyResolvers.forEach((fn) => fn());
      readyResolvers = [];
    });
  } catch {
    /* 网络/SDK 异常：降级为普通 H5 */
  }
}

export function updateShare(s: WxShareData): void {
  if (!ready || !wx) return;
  const payload = { title: s.title, desc: s.desc, link: s.link, imgUrl: s.imgUrl };
  wx.updateAppMessageShareData?.(payload);
  wx.updateTimelineShareData?.({ title: s.title, link: s.link, imgUrl: s.imgUrl });
}
